import type { Grant, YamlContent } from './YamlContent.ts'

export const USE_TOOLS_CAPABILITY = 'useTools'

const CAPABILITY_SEPARATOR = /[\s,;\|]+/

export interface McpCapabilityToken {
  hasCapability(source: string, capability: string): boolean
  getCounterparty(): string
  getSourceUrl(): URL
  getSrc(): string
}

export interface OAuthMcpContext {
  party?: string
  counterparty: string
  src: string
  scope: Record<string, string>
}

export class OAuthMcpToken implements McpCapabilityToken {
  #context: OAuthMcpContext

  constructor(context: OAuthMcpContext) {
    this.#context = context
  }

  hasCapability(source: string, capability: string): boolean {
    return (this.#context.scope[source] ?? '')
      .split(CAPABILITY_SEPARATOR)
      .includes(capability)
  }

  getParty(): string {
    return this.#context.party ?? ''
  }

  getCounterparty(): string {
    return this.#context.counterparty
  }

  getSourceUrl(): URL {
    return new URL(this.#context.src)
  }

  getSrc(): string {
    return this.#context.src
  }
}

export interface McpToolAuthorization {
  token: McpCapabilityToken | null
  providerSource: string
  toolName: string
  capability: string
  providerYaml?: YamlContent
}

export function hasMcpToolCapability(
  authorization: McpToolAuthorization,
): boolean {
  const {
    token,
    providerSource,
    toolName,
    capability,
    providerYaml,
  } = authorization

  if (!token) return false
  if (tokenHasCapability(token, providerSource, capability)) return true
  if (!providerYaml) return false

  const callerSource = String(token.getSourceUrl())
  return (
    tokenHasCapability(token, callerSource, USE_TOOLS_CAPABILITY) &&
    yamlDeclaresMcpToolCapability(providerYaml, toolName, capability) &&
    yamlGrantsCapability(
      providerYaml.grant,
      token.getCounterparty(),
      token.getSrc(),
      capability,
    )
  )
}

export function tokenHasCapability(
  token: { hasCapability(source: string, capability: string): boolean },
  source: string,
  capability: string,
): boolean {
  try {
    return token.hasCapability(source, capability)
  } catch (_e) {
    return false
  }
}

export function yamlDeclaresMcpToolCapability(
  yaml: Pick<YamlContent, 'mcp'>,
  toolName: string,
  capability: string,
): boolean {
  return Boolean(
    yaml.mcp?.tools.some((tool) =>
      tool.name === toolName && tool.capability === capability
    ),
  )
}

export function yamlGrantsCapability(
  grant: Grant | undefined,
  counterparty: string,
  src: string,
  capability: string,
): boolean {
  for (const counterpartyPattern in grant ?? {}) {
    if (
      !matchesSqlLike(
        counterparty.toLowerCase(),
        counterpartyPattern.toLowerCase(),
      )
    ) {
      continue
    }

    const grantEntry = grant![counterpartyPattern]
    if (Array.isArray(grantEntry)) {
      if (grantEntry.includes(capability)) return true
      continue
    }

    for (const srcPattern in grantEntry) {
      if (
        matchesSqlLike(src, srcPattern) &&
        splitCapabilities(grantEntry[srcPattern]).includes(capability)
      ) {
        return true
      }
    }
  }
  return false
}

function splitCapabilities(scope: string): string[] {
  return scope
    .split(CAPABILITY_SEPARATOR)
    .filter((capability) => capability.length > 0)
}

function matchesSqlLike(value: string, pattern: string): boolean {
  const regex = new RegExp(
    `^${
      pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/%/g, '.*')
        .replace(/_/g, '.')
    }$`,
  )
  return regex.test(value)
}
